import React, { useState, useEffect, useRef } from 'react';
import { ContentPopup } from 'components/ContentPopup.jsx';
import MaterialForm from 'components/frank/MaterialForm.jsx';
import { machineCount, setMachineCount } from 'components/frank/utils/machineSettings.js';

// The /mcc filter-island control for the owner's OWN MATERIAL prices (the "Malzeme" row), shown only on "My
// Manufacturing Lines". It is the material twin of ManageOwnMachinesButton: same button + ContentPopup + re-solve-
// on-close, but a material is a UserUnitPrice (a price), not a UserUnitPriceAttribute (machine), so the popup holds
// the usual material form (MaterialForm -> the settings SettingsTabs/QuoteBuilderForm) instead of a library picker,
// and the owner count comes back under `counts` rather than `attribute_counts`.
//
// RE-SOLVE ON CLOSE, NOT PER SAVE (same reasoning as the machine button): each add/edit/remove dispatches
// quote_builder.user_unit_price_saved / _deleted on `document`; the button only NOTES the change and re-solves the
// sections once when the popup CLOSES, so the owner can add several without the subtree remounting under them.
//
// Label reflects state: "+ Add Material" with none, "Manage" (gear) with some. Both open the material LIST (not a
// blank form): with no material the frequently-used ("Sık kullanılanlar") system materials lead (systemItemsFirst)
// so the owner picks one to price -- "Seç ve fiyat ekle" -- and with some the owner's own prices lead, the
// system list beneath. machineCount/setMachineCount from machineSettings are key-agnostic and read the
// window.frank_<key>_count global the page seeds for materials too.
//
// "SOME" MEANS SOME THAT FIT THE CURRENT BOBBIN STATE, not merely any material of this key: `hasCompatibleMaterial`
// (from the parent, which reads the section's own material list against the live bobbin state) decides the label
// when it is known -- so switching to Bobin with only non-bobbin carton rows reads "+ Add Material", because there
// is no bobbin row to manage. Until the parent's list has landed it is null, and the label falls back to the
// synchronous total count so nothing flashes. defaultBobbinEnabled seeds the new material's "bobin mi?" checkbox to
// the section's current bobbin state, so a material added while Bobin is on is a bobbin row (MaterialForm applies
// it), which is the whole point of leading the owner here.
const XHR = { 'X-Requested-With': 'XMLHttpRequest' };

const ManageOwnMaterialsButton = ({ materialKey, hasCompatibleMaterial, defaultBobbinEnabled }) => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(machineCount(materialKey));
  const changedRef = useRef(false);

  // Re-read the owner's own material count and keep the window global fresh. Materials come back under `counts`
  // (keyed by material key), NOT `attribute_counts` (which is machines).
  const refreshCount = () => {
    fetch('/quote_builder_user_unit_price_counts', { cache: 'no-store', headers: XHR })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || !data.counts) { return; }
        const next = data.counts[materialKey] || 0;
        setMachineCount(materialKey, next);
        setCount(next);
      })
      .catch(() => {});
  };

  const resolveIfChanged = () => {
    if (changedRef.current) {
      changedRef.current = false;
      window.dispatchEvent(new CustomEvent('frank:own-machines-changed'));
    }
  };

  useEffect(() => { refreshCount(); }, []);

  useEffect(() => {
    const onChanged = () => { changedRef.current = true; refreshCount(); };
    document.addEventListener('quote_builder.user_unit_price_saved', onChanged);
    document.addEventListener('quote_builder.user_unit_price_deleted', onChanged);
    return () => {
      document.removeEventListener('quote_builder.user_unit_price_saved', onChanged);
      document.removeEventListener('quote_builder.user_unit_price_deleted', onChanged);
    };
  }, []);

  // Safety net: torn down mid-edit by some OTHER remount (not the close path) -- still re-solve for what changed.
  useEffect(() => resolveIfChanged, []);

  const closePopup = () => {
    setOpen(false);
    resolveIfChanged();
  };

  // Prefer the parent's bobbin-aware "has a material that FITS" signal; fall back to the total count while it is
  // still unknown (null), so the label never flashes on first paint.
  const hasNone = hasCompatibleMaterial == null ? count === 0 : !hasCompatibleMaterial;

  return (
    <>
      <button
        type="button"
        className="dct-button dct-button--secondary dct-button--small"
        onClick={() => setOpen(true)}
        data-manage-materials={materialKey}
      >
        {hasNone ? (
          <><i className="fa fa-plus" aria-hidden="true" /> {t('quote_builder.add_material')}</>
        ) : (
          <><i className="fa fa-cog" aria-hidden="true" /> {t('quote_builder.manage_materials')}</>
        )}
      </button>
      {open && (
        <ContentPopup
          autoOpen
          beforeClose={closePopup}
          modalStyle={{ width: '90%', maxWidth: '1000px' }}
          content={(
            // Both open the material list: with none, the frequently-used system materials lead (pick one to
            // price); with some, the owner's own prices lead (edit / remove / add more). systemItemsFirst only
            // takes effect when the own list is empty, so it is safe to pass unconditionally.
            <MaterialForm materialKey={materialKey} systemItemsFirst defaultBobbinEnabled={defaultBobbinEnabled} />
          )}
        />
      )}
    </>
  );
};

export default ManageOwnMaterialsButton;
